import {
  type TurnDirection,
  TURN_DIRECTIONS,
  TURN_DIRECTION_LABELS,
  TURN_COST,
} from "@/program/Card";
import { getTurnDirectionSVG } from "./turnIconSVG";

/**
 * 方向選択モーダル
 */
export class TurnSelectorModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private onSelectCallback?: (direction: TurnDirection) => void;
  private onCloseCallback?: () => void;
  private isOpen: boolean = false;
  private previousFocus: HTMLElement | null = null;

  constructor() {
    // オーバーレイ
    this.overlay = document.createElement("div");
    this.overlay.className = "turn-modal-overlay";
    this.overlay.addEventListener("click", () => this.close());

    // モーダル本体
    this.modal = document.createElement("div");
    this.modal.className = "turn-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-label", "方向を選択");
    this.modal.addEventListener("click", (e) => e.stopPropagation());

    this.overlay.appendChild(this.modal);
  }

  /**
   * モーダルを開く
   */
  open(current: TurnDirection | null): void {
    if (this.isOpen) return;
    this.isOpen = true;

    this.previousFocus = document.activeElement as HTMLElement | null;
    this.render(current);
    document.body.appendChild(this.overlay);

    // ESCキーで閉じる / フォーカストラップ
    document.addEventListener("keydown", this.handleKeyDown);

    // 最初のフォーカス可能な要素にフォーカス
    requestAnimationFrame(() => {
      const first = this.modal.querySelector("button:not([disabled])");
      if (first instanceof HTMLElement) first.focus();
    });
  }

  /**
   * モーダルを閉じる
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.remove();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.previousFocus?.focus();
    this.onCloseCallback?.();
  }

  /**
   * モーダルが開いているか
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * 選択時コールバック
   */
  onSelect(callback: (direction: TurnDirection) => void): void {
    this.onSelectCallback = callback;
  }

  /**
   * 閉じた時コールバック
   */
  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  /**
   * ESCキーハンドラ
   */
  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "Tab") {
      this.trapFocus(e);
    }
  };

  /** モーダル内でフォーカスを循環させる */
  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.modal.querySelectorAll("button:not([disabled])");
    if (focusable.length === 0) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /**
   * モーダル内容を描画
   */
  private render(current: TurnDirection | null): void {
    const children: HTMLElement[] = [];

    // タイトル
    const title = document.createElement("div");
    title.className = "turn-modal__title";
    title.textContent = "方向を選択";
    children.push(title);

    // 方向選択肢グリッド
    const grid = document.createElement("div");
    grid.className = "turn-modal__grid";

    for (const direction of TURN_DIRECTIONS) {
      const isSelected = current !== null && direction === current;

      const button = document.createElement("button");
      button.className = "turn-modal__option";
      if (isSelected) button.classList.add("turn-modal__option--selected");
      button.setAttribute(
        "aria-label",
        `${TURN_DIRECTION_LABELS[direction]}（コスト${TURN_COST}）`
      );

      // アイコン
      const iconSpan = document.createElement("span");
      iconSpan.className = "turn-direction-icon";
      iconSpan.setAttribute("aria-hidden", "true");
      iconSpan.innerHTML = getTurnDirectionSVG(direction);
      button.appendChild(iconSpan);

      // ラベル
      const labelSpan = document.createElement("span");
      labelSpan.className = "turn-modal__label";
      labelSpan.textContent = TURN_DIRECTION_LABELS[direction];
      button.appendChild(labelSpan);

      // コスト
      const costSpan = document.createElement("span");
      costSpan.className = "turn-modal__cost";
      costSpan.textContent = `${TURN_COST}MP`;
      button.appendChild(costSpan);

      button.addEventListener("click", () => {
        this.onSelectCallback?.(direction);
        this.close();
      });

      grid.appendChild(button);
    }

    children.push(grid);

    // 閉じるボタン
    const closeBtn = document.createElement("button");
    closeBtn.className = "turn-modal__close";
    closeBtn.textContent = "キャンセル";
    closeBtn.addEventListener("click", () => this.close());
    children.push(closeBtn);

    this.modal.replaceChildren(...children);
  }
}
